import React, { useState, useEffect } from 'react';
import { reserveMultipleTickets } from '../services/raffleService';
import { sendTicketNotification } from '../services/notificationService';
import { useSpring, animated } from '@react-spring/web';
import toast from 'react-hot-toast';

const PremiumMultiTicketModal = ({ tickets, raffle, raffleId, onClose }) => {
  const [buyerInfo, setBuyerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [partialPayment, setPartialPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(tickets.length * raffle.price);

  const modalAnimation = useSpring({
    from: { opacity: 0, transform: 'scale(0.9) translateY(50px)' },
    to: { opacity: 1, transform: 'scale(1) translateY(0px)' },
    config: { tension: 300, friction: 25 }
  });

  const overlayAnimation = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 }
  });

  const totalPrice = tickets.length * raffle.price;
  const balance = totalPrice - paymentAmount;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!buyerInfo.name || !buyerInfo.phone) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    if (!selectedPaymentMethod) {
      toast.error('Por favor selecciona un método de pago');
      return;
    }

    if (partialPayment && (paymentAmount <= 0 || paymentAmount > totalPrice)) {
      toast.error('Por favor ingresa un monto de pago válido');
      return;
    }

    setLoading(true);
    
    try {
      const ticketData = {
        ...buyerInfo,
        totalPaid: paymentAmount,
        balance: balance,
        paymentHistory: [{
          amount: paymentAmount,
          date: new Date().toISOString(),
          method: selectedPaymentMethod
        }]
      };

      await reserveMultipleTickets(raffleId, tickets, ticketData);

      const ticketNumbers = tickets.map(t => t.displayNumbers).join(', ');
      await sendTicketNotification(raffleId, ticketNumbers, buyerInfo.name);
      
      toast.success(
        <div className="flex items-center">
          <span className="text-2xl mr-2">🎉</span>
          <span>¡{tickets.length} boletos reservados exitosamente!</span>
        </div>,
        { duration: 5000 }
      );
      
      onClose();
    } catch (error) {
      console.error('Error reserving tickets:', error);
      toast.error('Error al reservar los boletos. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      cash: '💵',
      transfer: '🏦',
      crypto: '₿',
      nequi: '📱',
      daviplata: '📲',
      qr: '📷'
    };
    return icons[method] || '💳';
  };

  const enabledPaymentMethods = raffle.paymentMethods?.filter(m => m.enabled) || [];

  return (
    <animated.div 
      style={overlayAnimation}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <animated.div 
        style={modalAnimation}
        className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 p-8 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full mb-4">
              <span className="text-3xl font-bold">{tickets.length}</span>
            </div>
            <h2 className="text-2xl font-bold mb-2">Reservar {tickets.length} Boletos</h2>
            <p className="text-white/80">Has seleccionado múltiples números de la suerte</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Selected Tickets */}
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Números seleccionados:</h3>
            <div className="flex flex-wrap gap-2">
              {tickets.map((ticket) => (
                <span 
                  key={ticket.id}
                  className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-xl font-bold shadow-md"
                >
                  {ticket.displayNumbers}
                </span>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Info Section */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3 text-sm">1</span>
                Información Personal
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    value={buyerInfo.name}
                    onChange={(e) => setBuyerInfo({...buyerInfo, name: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all"
                    placeholder="Juan Pérez"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    value={buyerInfo.phone}
                    onChange={(e) => setBuyerInfo({...buyerInfo, phone: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all"
                    placeholder="300 123 4567"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={buyerInfo.email}
                    onChange={(e) => setBuyerInfo({...buyerInfo, email: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all"
                    placeholder="correo@ejemplo.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dirección
                  </label>
                  <input
                    type="text"
                    value={buyerInfo.address}
                    onChange={(e) => setBuyerInfo({...buyerInfo, address: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all"
                    placeholder="Calle 123 #45-67"
                  />
                </div>
              </div>
            </div>

            {/* Payment Section */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mr-3 text-sm">2</span>
                Información de Pago
              </h3>
              
              {/* Payment Type Toggle */}
              <div className="mb-4 p-1 bg-gray-100 rounded-xl inline-flex">
                <button
                  type="button"
                  onClick={() => {
                    setPartialPayment(false);
                    setPaymentAmount(totalPrice);
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    !partialPayment ? 'bg-white text-green-600 shadow-md' : 'text-gray-600'
                  }`}
                >
                  Pago Total
                </button>
                <button
                  type="button"
                  onClick={() => setPartialPayment(true)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    partialPayment ? 'bg-white text-orange-600 shadow-md' : 'text-gray-600'
                  }`}
                >
                  Abono Parcial
                </button>
              </div>
              
              {/* Payment Amount */}
              {partialPayment && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monto a abonar
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(Number(e.target.value))}
                      max={totalPrice}
                      min={1}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-green-200 focus:border-green-500 transition-all"
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Máximo: ${totalPrice.toLocaleString('es-CO')}
                  </p>
                </div>
              )}
              
              {/* Payment Methods */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {enabledPaymentMethods.map((method) => (
                  <button
                    key={method.type}
                    type="button"
                    onClick={() => {
                      setSelectedPaymentMethod(method.type);
                      setShowPaymentDetails(true);
                    }}
                    className={`
                      relative p-4 rounded-xl border-2 transition-all text-center
                      ${selectedPaymentMethod === method.type 
                        ? 'border-green-500 bg-green-50 shadow-lg transform scale-105' 
                        : 'border-gray-200 bg-white hover:border-green-300 hover:shadow-md'
                      }
                    `}
                  >
                    {selectedPaymentMethod === method.type && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                    <div className="text-2xl mb-1">{getPaymentMethodIcon(method.type)}</div>
                    <div className="text-sm font-medium text-gray-700">
                      {method.type === 'cash' && 'Efectivo'}
                      {method.type === 'transfer' && 'Transferencia'}
                      {method.type === 'crypto' && 'Crypto'}
                      {method.type === 'nequi' && 'Nequi'}
                      {method.type === 'daviplata' && 'Daviplata'}
                      {method.type === 'qr' && 'QR Code'}
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Payment Details */}
              {showPaymentDetails && selectedPaymentMethod && (
                <div className="mt-4 p-4 bg-white rounded-xl border-2 border-green-200">
                  {raffle.paymentMethods.find(m => m.type === selectedPaymentMethod)?.details && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Detalles de pago:</p>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">
                        {raffle.paymentMethods.find(m => m.type === selectedPaymentMethod).details}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Summary Section */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Resumen de tu compra</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Cantidad de boletos</span>
                  <span className="font-bold text-lg">{tickets.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Precio por boleto</span>
                  <span className="font-medium">${raffle.price.toLocaleString('es-CO')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">${totalPrice.toLocaleString('es-CO')}</span>
                </div>
                
                {partialPayment && (
                  <>
                    <div className="border-t pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Abono</span>
                        <span className="font-bold text-green-600">
                          ${paymentAmount.toLocaleString('es-CO')}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-gray-600">Saldo pendiente</span>
                        <span className="font-bold text-orange-600">
                          ${balance.toLocaleString('es-CO')}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-lg">Total a pagar ahora</span>
                    <span className="font-bold text-2xl text-purple-600">
                      ${paymentAmount.toLocaleString('es-CO')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || !selectedPaymentMethod}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Procesando...
                  </span>
                ) : (
                  'Confirmar Reserva'
                )}
              </button>
            </div>
          </form>
        </div>
      </animated.div>
    </animated.div>
  );
};

export default PremiumMultiTicketModal;